import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState, useMemo, useCallback } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Seo } from "src/components/seo";
import { LoadingButton } from "@mui/lab";
import LinearProgress from "@mui/material/LinearProgress";
import SvgIcon from "@mui/material/SvgIcon";
import CheckCircleSolidIcon from "src/icons/untitled-ui/duocolor/check-circle-solid";
import PlacesAutocomplete from "react-places-autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import LogoLoader from "./logo-loader";
import { SeverityPill } from "src/components/severity-pill";
import useHousehold from "src/contexts/household-context";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { useLocation } from "react-router-dom";
import { parseStateAndPostalCode } from "src/utils/snug";
import Button from "@mui/material/Button";
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import type { SpouseRelationshipTypes, UserDataObj } from "src/types/snugtotal";
import { Layout as ClassicLayout } from "src/layouts/auth/classic-layout";
import { PersonalPlanSelector } from "./personal-plan-selector";
import useUserDataProvider from "src/contexts/userData-context";

const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

const stateSummaries: Record<string, string> = {
  AL: `Alabama is a common law property state. Probate is typically 6 to 12 months if over $25,000 in assets. There are no state estate or inheritance taxes.`,
  AK: `Alaska is a opt-in community property state. Probate is typically 6 to 12 months if over $100k vehicles and $50k other property in assets. There are no state estate or inheritance taxes.`,
  AZ: `Arizona is a community property state. Probate is typically 9 to 18 months if over $75k personal and $100k real estate in assets. There are no state estate or inheritance taxes.`,
  AR: `Arkansas is a common law property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  CA: `California is a community property state. Probate is typically 12 to 24 months if over $184,500 in assets. There are no state estate or inheritance taxes.`,
  CO: `Colorado is a common law property state. Probate is typically 6 to 12 months if over $82,000 in assets. There are no state estate or inheritance taxes.`,
  CT: `Connecticut is a common law property state. Probate is typically 9 to 18 months if over $40,000 in assets. There are state estate taxes of 12% over $13.99M.`,
  DE: `Delaware is a common law property state. Probate is typically 9 to 12 months if over $30,000 in assets. There are no state estate or inheritance taxes.`,
  DC: `District of Columbia is a common law property state. Probate is typically 9 to 18 months if over $40,000 in assets. There are state estate taxes of up to 16% over $4.873M.`,
  FL: `Florida is a common law property state. Probate is typically 9 to 18 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  GA: `Georgia is a common law property state. Probate is typically 6 to 12 months if over $10,000 in assets. There are no state estate or inheritance taxes.`,
  HI: `Hawaii is a common law property state. Probate is typically 9 to 18 months if over $100,000 (vehicles excluded) in assets. There are state estate taxes of up to 20% over $5.49 M.`,
  ID: `Idaho is a community property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  IL: `Illinois is a common law property state. Probate is typically 9 to 18 months if over $100,000 in assets. There are state estate taxes of up to 16% over $4 M.`,
  IN: `Indiana is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  IA: `Iowa is a common law property state. Probate is typically 9 to 12 months if over $200k in assets. There are no state estate or inheritance taxes.`,
  KS: `Kansas is a common law property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  KY: `Kentucky is a common law property state. Probate is typically 6 to 12 months if over $15,000 in assets. There are state inheritance taxes of up to 16% over $500, depending on heir class.`,
  LA: `Louisiana is a community property state. Probate is typically 9 to 18 months if over $125,000 in assets. There are no state estate or inheritance taxes.`,
  ME: `Maine is a common law property state. Probate is typically 6 to 12 months if over $40,000 in assets. There are state estate taxes of up to 12% over $7M.`,
  MD: `Maryland is a common law property state. Probate is typically 9 to 18 months if over $50k in assets ($100k if all to spouse). There are state estate taxes of up to 16% over $5M and inheritance taxes of 10% on non-exempt heirs over $1k.`,
  MA: `Massachusetts is a common law property state. Probate is typically 9 to 12 months if over $25,000 in assets and vehicle. There are state estate taxes of up to 16% over $2M.`,
  MI: `Michigan is a common law property state. Probate is typically 6 to 12 months if over $25,000 in assets and allowances. There are no state estate or inheritance taxes.`,
  MN: `Minnesota is a common law property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are state estate taxes of up to 16% over $3M.`,
  MS: `Mississippi is a common law property state. Probate is typically 9 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  MO: `Missouri is a common law property state. Probate is typically 6 to 12 months if over $40,000 in assets. There are no state estate or inheritance taxes.`,
  MT: `Montana is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  NE: `Nebraska is a common law property state. Probate is typically 6 to 12 months if over $100k in assets. There are state inheritance taxes of up to 15% over $25k, depending on heir class.`,
  NV: `Nevada is a community property state. Probate is typically 9 to 12 months if over $25k in assets ($100k if all to spouse). There are no state estate or inheritance taxes.`,
  NH: `New Hampshire is a common law property state. Probate is typically 9 to 12 months if over $10,000 in assets. There are no state estate or inheritance taxes.`,
  NJ: `New Jersey is a common law property state. Probate is typically 9 to 18 months if over $20k in assets ($50k if all to spouse). There are state inheritance taxes of up to 16% over $25k, depending on heir class.`,
  NM: `New Mexico is a community property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  NY: `New York is a common law property state. Probate is typically 9 to 24 months if over $50,000 in assets. There are state estate taxes of up to 16% over $7.16M.`,
  NC: `North Carolina is a common law property state. Probate is typically 6 to 12 months if over $20k in assets ($30k if all to spouse). There are no state estate or inheritance taxes.`,
  ND: `North Dakota is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  OH: `Ohio is a common law property state. Probate is typically 9 to 12 months if over $35k in assets ($100k if all to spouse). There are no state estate or inheritance taxes.`,
  OK: `Oklahoma is a common law property state. Probate is typically 6 to 12 months if over $200k in assets. There are no state estate or inheritance taxes.`,
  OR: `Oregon is a common law property state. Probate is typically 9 to 18 months if over $75k in personal assets and $200k in real estate. There are state estate taxes of up to 16% over $1M.`,
  PA: `Pennsylvania is a common law property state. Probate is typically 9 to 18 months if over $50,000 in assets. There are state inheritance taxes of up to 15%, depending on heir class.`,
  RI: `Rhode Island is a common law property state. Probate is typically 9 to 12 months if over $15,000 in assets. There are state estate taxes of up to 16% over $1.802M.`,
  SC: `South Carolina is a common law property state. Probate is typically 9 to 12 months if over $25,000 in assets. There are no state estate or inheritance taxes.`,
  SD: `South Dakota is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  TN: `Tennessee is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  TX: `Texas is a community property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  UT: `Utah is a common law property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  VT: `Vermont is a common law property state. Probate is typically 9 to 12 months if over $45k in assets and no real estate. There are state estate taxes of up to 16% over $5M.`,
  VA: `Virginia is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  WA: `Washington is a community property state. Probate is typically 9 to 12 months if over $100,000 in assets. There are state estate taxes of up to 20% over $2.193M.`,
  WV: `West Virginia is a common law property state. Probate is typically 6 to 12 months if over $50k in personal assets and $100k in real estate. There are no state estate or inheritance taxes.`,
  WI: `Wisconsin is a community property state. Probate is typically 9 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  WY: `Wyoming is a common law property state. Probate is typically 6 to 12 months if over $200,000 in assets. There are no state estate or inheritance taxes.`,
};

interface PersonalPlanOption {
  description: string;
  title: string;
  plural: string;
  value: 'joint_trust' | 'single_trust' | 'two_trusts' | 'single_will' | 'two_wills' | 'attorney';
}

const personalPlanOptions: PersonalPlanOption[] = [
  {
    description:
      "A Joint Revocable Living Trust will cover both of you with a single, comprehensive Trust that can keep you out of probate.",
    title: "Joint Trust",
    plural: "1x",
    value: "joint_trust",
  },
  {
    description:
      "Revocable Living Trusts are a good choice when you want to avoid probate and have individual control over beneficiaries, trustees, etc.",
    title: "Individual Trust",
    plural: "2x",
    value: "two_trusts",
  },
    {
    description:
      "Revocable Living Trusts are a good choice when you want to avoid probate and have individual control over beneficiaries, trustees, etc.",
    title: "Individual Trust",
    plural: "2x",
    value: "single_trust",
  },
  {
    description:
      "Last Will & Testaments are quick and easy to put in place, but are subject to probate and are part of the public record.",
    title: "Individual Will",
    plural: "2x",
    value: "single_will",
  },
  {
    description:
      "Last Will & Testaments are quick and easy to put in place, but are subject to probate and are part of the public record.",
    title: "Individual Will",
    plural: "2x",
    value: "two_wills",
  },
  {
    description:
      "Upload an existing Will or Trust for secure storage and easy sharing with family, friends, and professionals.",
    title: "Upload Attorney Documents",
    plural: "",
    value: "attorney",
  },
];

// Create a wrapper component to pass the onBack handler
const OnboardingContent = () => {
  const { userData, handleAddOrEditOwnerUserData } = useUserDataProvider();
  const {
    selectedHousehold,
    headOfHouseholDataSaving,
    onboardingHandleEditHoHUserData,
  } = useHousehold();
  const headOfHousehold = selectedHousehold?.head_of_household;
  const isLoggedInUserHoH = !headOfHousehold || userData?.ud_id === headOfHousehold?.ud_id;
  const isLoggedInUserSpouse = !isLoggedInUserHoH && Boolean(
    selectedHousehold?.household_member_roles?.find(
      (role: any) => role.role_target_user_data_ud_id === userData?.ud_id && role.role === "SPOUSE"
    )
  );
  const {
    peopleRoles,
    peopleRolesSavingNew,
    peopleRolesLoading,
    targetUserDatas,
    handleAddOrUpdatePeopleRolesAndUserData,
  } = usePeopleRolesStore(headOfHousehold?.ud_id || null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const returnPath = queryParams.get("returnPath");
  const spouse_role = peopleRoles?.find((role: any) => role.role === "SPOUSE");
  const [targetUserData, setTargetUserData] = useState<UserDataObj | null>(
    null
  );
  const router = useRouter();
  useEffect(() => {
    setTargetUserData(
      targetUserDatas?.find(
        (d) => d.ud_id === spouse_role?.role_target_user_data_ud_id
      ) || null
    );
  }, [spouse_role, targetUserDatas]);
  const [personalPlan, setPersonalPlan] = useState<'single_trust' | 'two_trusts' | 'joint_trust' | 'single_will' | 'two_wills' | 'attorney' | null>(
    headOfHousehold?.estate_plan_foundation || null
  );

  const subscriptionStatus = 'blocked';

  const [mode, setMode] = useState<"onboarding" | "edit">("onboarding");

  // Determine the appropriate starting step based on completed fields
  const getStartingStep = useMemo(() => {
    if (!headOfHousehold) return "marital_status";

    if (mode === "edit") {
      return "marital_status"; // Force back to this step when editing
    }

    // Detect marital status
    const hasMaritalStatus =
      spouse_role?.spouse_relationship ||
      (!spouse_role &&
      (headOfHousehold.children ||
        headOfHousehold.value_of_assets ||
        headOfHousehold.household_state_code ||
        headOfHousehold.estate_plan_foundation)
        ? "single"
        : undefined);

    const hasChildren =
      headOfHousehold.children !== null &&
      headOfHousehold.children !== undefined;
    const hasBlendedFamily =
      headOfHousehold.children === "none" ||
      (headOfHousehold.blended_family !== null &&
        headOfHousehold.blended_family !== undefined);
    const hasResidence = Boolean(
      headOfHousehold.address_text || headOfHousehold.household_state_code
    );
    const hasAssetValue = Boolean(headOfHousehold.value_of_assets);
    const hasGoals = Array.isArray(headOfHousehold.onboarding_goals);
    const hasSituations = Array.isArray(headOfHousehold.special_situations);
    const allPrerequisitesComplete =
      hasMaritalStatus &&
      hasChildren &&
      hasBlendedFamily &&
      hasResidence &&
      hasAssetValue &&
      hasGoals &&
      hasSituations;

    if (allPrerequisitesComplete) {
      return "personal_plan";
    }

    return "marital_status";
  }, [headOfHousehold, spouse_role, mode]);

  const [onboardingStep, setOnboardingStep] = useState<string>("household");

  // Check for step query parameter on mount
  useEffect(() => {
    const stepParam = queryParams.get("step");
    if (stepParam) {
      setOnboardingStep(stepParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onboardingStep === "loader") {
      const timer = setTimeout(() => {
        // move to step personal plan after 6 seconds
        setOnboardingStep("personal_plan");
      }, 6000);
      return () => clearTimeout(timer); // cleanup
    }
    if (onboardingStep === "logo_loader") {
      const timer = setTimeout(() => {
        // move to next step after 3 seconds
        setOnboardingStep("goals");
      }, 5000);
      return () => clearTimeout(timer); // cleanup
    }
  }, [onboardingStep]);

  // Update onboarding step when data changes
  useEffect(() => {
    const newStep = getStartingStep;
    if (newStep !== onboardingStep && onboardingStep === "marital_status") {
      setOnboardingStep(newStep);
    }
  }, [getStartingStep, onboardingStep]);

  // Scroll to top when onboarding step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [onboardingStep]);

  // Auto-skip premium steps if subscription status is not "offered"
  // useEffect(() => {
  //   if (subscriptionStatus !== "offered") {
  //     // If user somehow lands on a premium step, skip to the appropriate step
  //     if (
  //       onboardingStep === "premium" ||
  //       onboardingStep === "assets" ||
  //       onboardingStep === "access"
  //     ) {
  //       setOnboardingStep("loader");
  //     }
  //   }
  // }, [onboardingStep, subscriptionStatus]);

  const [progress, setProgress] = useState<number>(25);
  useEffect(() => {
    // Determine if we're showing premium steps
    // const showPremiumSteps = subscriptionStatus === "offered";
    const showPremiumSteps = false;

    // Household screen (includes all household substeps)
    if (
      onboardingStep === "household" ||
      onboardingStep === "marital_status" ||
      onboardingStep === "spouse_name" ||
      onboardingStep === "children_status" ||
      onboardingStep === "blended_family" ||
      onboardingStep === "residence"
    ) {
      setProgress(25);
    }

    // Goals screen
    if (onboardingStep === "goals") {
      setProgress(showPremiumSteps ? 45 : 60);
    }
    // Situations screen
    if (onboardingStep === "situations") {
      setProgress(showPremiumSteps ? 55 : 80);
    }

    // // Premium screen (only shown if subscriptionStatus is "offered")
    // if (onboardingStep === "premium" && showPremiumSteps) {
    //   setProgress(65);
    // }
    // // Assets screen (only shown if subscriptionStatus is "offered")
    // if (onboardingStep === "assets" && showPremiumSteps) {
    //   setProgress(80);
    // }
    // // Access screen (only shown if subscriptionStatus is "offered")
    // if (onboardingStep === "access" && showPremiumSteps) {
    //   setProgress(90);
    // }

    // Personal plan / loader
    if (
      onboardingStep === "personal_plan" ||
      onboardingStep === "loader" ||
      onboardingStep === "logo_loader"
    ) {
      setProgress(100);
    }
  }, [onboardingStep, subscriptionStatus]);

  const [stateOfResidence, setStateOfResidence] = useState<string>(
    selectedHousehold?.head_of_household?.household_state_code || ""
  );
  const [summary, setSummary] = useState<string>(
    () => stateSummaries[stateOfResidence as keyof typeof stateSummaries] || ""
  );
  useEffect(() => {
    setSummary(
      stateSummaries[stateOfResidence as keyof typeof stateSummaries] ?? ""
    );
  }, [stateOfResidence]);

  const [maritalStatus, setMaritalStatus] = useState<string>("");
  const [spouseName, setSpouseName] = useState<string>("");
  const [spouseEmail, setSpouseEmail] = useState<string>("");
  const [spouseNameError, setSpouseNameError] = useState<string>("");
  const [spouseEmailError, setSpouseEmailError] = useState<string>("");
  const [showSpouseEmail, setShowSpouseEmail] = useState<boolean>(false);
  const [hasExistingSpouseEmail, setHasExistingSpouseEmail] =
    useState<boolean>(false);

  // Plan selector state
  const [willType, setWillType] = useState<"single_will" | "two_wills">("two_wills");
  const [trustType, setTrustType] = useState<"joint" | "two_trusts" | "single_trust">(
    "joint"
  );

  // Populate spouse fields from existing spouse role data
  useEffect(() => {
    if (spouse_role) {
      setMaritalStatus(spouse_role.spouse_relationship || "");
      if (isLoggedInUserSpouse) {
        // Spouse is logged in: "spouse" fields show HoH (their spouse) data
        setSpouseName(headOfHousehold?.full_name || "");
        const existingEmail = headOfHousehold?.contact_email || "";
        setSpouseEmail(existingEmail);
        if (existingEmail) {
          setHasExistingSpouseEmail(true);
        }
      } else if (targetUserData) {
        // HoH is logged in: "spouse" fields show the spouse's data
        setSpouseName(targetUserData.full_name || "");
        const existingEmail = targetUserData.contact_email || "";
        setSpouseEmail(existingEmail);
        if (existingEmail) {
          setHasExistingSpouseEmail(true);
        }
      }
    } else if (
      headOfHousehold &&
      (headOfHousehold.children ||
        headOfHousehold.value_of_assets ||
        headOfHousehold.household_state_code ||
        headOfHousehold.estate_plan_foundation)
    ) {
      // If no spouse role but other fields are filled, assume single
      setMaritalStatus("single");
      setSpouseName("");
      setSpouseEmail("");
      setHasExistingSpouseEmail(false);
    } else {
      // Clear all fields if no data
      setMaritalStatus("");
      setSpouseName("");
      setSpouseEmail("");
      setHasExistingSpouseEmail(false);
    }
  }, [spouse_role, targetUserData, headOfHousehold, isLoggedInUserSpouse]);

  // Pre-select plan type when professional has chosen via direct_charge_document_selection
  // useEffect(() => {
  //   const directCharge =
  //     selectedHousehold?.household_role?.direct_charge_document_selection;

  //   if (directCharge === "joint") {
  //     setTrustType("joint");
  //   } else if (directCharge === "trust") {
  //     setTrustType("two_trusts"); // Default to 2x individual trusts for couples
  //   } else if (directCharge === "will") {
  //     setWillType("two_wills"); // Default to both wills for couples
  //   }
  // }, [selectedHousehold?.household_role?.direct_charge_document_selection]);

  const handleChangeMaritalStatus = (e: any) => {
    setMaritalStatus(e.target.value);
  };

  const [childrenStatus, setChildrenStatus] = useState<string>(
    headOfHousehold?.children || ""
  );
  useEffect(() => {
    setChildrenStatus(headOfHousehold?.children || "");
  }, [headOfHousehold?.children]);
  const handleChangeChildrenStatus = (e: any) => {
    setChildrenStatus(e.target.value);
  };

  const [blendedFamily, setBlendedFamily] = useState<string>(
    headOfHousehold?.blended_family === true
      ? "blended"
      : headOfHousehold?.blended_family === false
      ? "not"
      : ""
  );
  useEffect(() => {
    // Only update if blended_family is not null (i.e., it has been explicitly set)
    if (
      headOfHousehold?.blended_family !== null &&
      headOfHousehold?.blended_family !== undefined
    ) {
      setBlendedFamily(
        headOfHousehold.blended_family === true ? "blended" : "not"
      );
    } else {
      setBlendedFamily("");
    }
  }, [headOfHousehold?.blended_family]);
  const handleChangeBlendedFamily = (value: string) => {
    setBlendedFamily(value);
  };

  // Clear blended family when children is set to "none"
  useEffect(() => {
    if (
      headOfHousehold?.children === "none" &&
      headOfHousehold?.blended_family !== null &&
      headOfHousehold?.ud_id
    ) {
      onboardingHandleEditHoHUserData(headOfHousehold.ud_id, {
        blended_family: null,
      });
    }
  }, [
    headOfHousehold?.children,
    headOfHousehold?.blended_family,
    headOfHousehold?.ud_id,
    onboardingHandleEditHoHUserData,
  ]);

  useEffect(() => {
    setPersonalPlan(headOfHousehold?.estate_plan_foundation || null);
  }, [headOfHousehold?.estate_plan_foundation]);

  const [recommendedPlan, setRecommendedPlan] = useState<string | null>(null);
  const [recommendedPlanExplainer, setRecommendedPlanExplainer] = useState<
    string | null
  >(null);

  const [checkedGoals, setCheckedGoals] = useState({
    avoidProbate: false,
    privacy: false,
    minimizeTaxes: false,
    easySetup: false,
    guardianship: false,
    incapacity: false,
  });

  // Populate goals from database
  useEffect(() => {
    if (headOfHousehold?.onboarding_goals) {
      const goals = headOfHousehold.onboarding_goals;
      setCheckedGoals({
        avoidProbate: goals.includes("avoidProbate"),
        privacy: goals.includes("privacy"),
        minimizeTaxes: goals.includes("minimizeTaxes"),
        easySetup: goals.includes("easySetup"),
        guardianship: goals.includes("guardianship"),
        incapacity: goals.includes("incapacity"),
      });
    }
  }, [headOfHousehold?.onboarding_goals]);

  const handleGoalChange =
    (key: keyof typeof checkedGoals) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedGoals((prev) => ({
        ...prev,
        [key]: event.target.checked,
      }));
    };

  const [checkedSituations, setCheckedSituations] = useState({
    nonUs: false,
    spouseNonUs: false,
    charity: false,
    divorce: false,
    international: false,
    creditors: false,
    fight: false,
    none: false,
  });

  // Populate situations from database
  useEffect(() => {
    if (!headOfHousehold?.special_situations) return;

    const situations = headOfHousehold.special_situations;

    // 1️⃣  Work out every specific flag
    const status = {
      nonUs: situations.includes("nonUs"),
      spouseNonUs: situations.includes("spouseNonUs"),
      charity: situations.includes("charity"),
      divorce: situations.includes("divorce"),
      international: situations.includes("international"),
      creditors: situations.includes("creditors"),
      fight: situations.includes("fight"),
      none: situations.includes("none"),
    };

    // 2️⃣  Set situations and mark as interacted if data exists
    setCheckedSituations({
      ...status,
    });
  }, [headOfHousehold?.special_situations]);

  const handleSituationChange =
    (key: keyof typeof checkedSituations) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedSituations((prev) => {
        const newState = { ...prev };

        if (key === "none") {
          // If selecting "none", uncheck all other options
          if (event.target.checked) {
            Object.keys(newState).forEach((k) => {
              if (k !== "none")
                newState[k as keyof typeof checkedSituations] = false;
            });
          }
          newState.none = event.target.checked;
        } else {
          // If selecting any other option, uncheck "none"
          if (event.target.checked) {
            newState.none = false;
          }
          newState[key] = event.target.checked;
        }

        return newState;
      });
    };

  // props or data you already have
  const allowWills = selectedHousehold?.professional_group?.allow_wills; // global
  const allowTrusts = selectedHousehold?.professional_group?.allow_trusts; // global
  const blockWill = selectedHousehold?.household_role?.block_will; // per-client
  const blockTrust = selectedHousehold?.household_role?.block_trust; // per-client

  // ✨ handy one-liners ----------------------------------------------------------
  const canWill = (allowWills ?? true) && !(blockWill ?? false); // true → this client may create a Will
  const canTrust = (allowTrusts ?? true) && !(blockTrust ?? false); // true → this client may create a Trust

  // Apply direct_charge_document_selection constraint if professional has pre-selected a plan
  // const directChargeSelection =
  //   selectedHousehold?.household_role?.direct_charge_document_selection;
  // const constrainedCanWill =
  //   canWill && (!directChargeSelection || directChargeSelection === "will");
  // const constrainedCanTrust =
  //   canTrust &&
  //   (!directChargeSelection ||
  //     directChargeSelection === "trust" ||
  //     directChargeSelection === "joint");

  const isAttorney =
    selectedHousehold?.professional_group?.professional_classification?.includes(
      "ATTORNEY"
    );

  // Membership configuration
  // const allowMembership =
  //   selectedHousehold?.household_role?.allow_membership ?? false;

  // ⛳ only keep the plans the client is allowed to choose
  const personalPlanOptionsAllowed = useMemo(
    () =>
      personalPlanOptions.filter((opt) => {
        if (opt.value === "single_will" || opt.value === "two_wills") return canWill;
        if (opt.value === "single_trust" || opt.value === "two_trusts" || opt.value === "joint_trust")
          return canTrust;
        if (opt.value === "attorney") return !isAttorney;
        return true; // 'attorney' and 'skip' are always OK
      }),
    [canWill, canTrust, isAttorney]
  );

  const isPlanAllowed = useCallback(
    (plan: string) => {
      if (plan === "will") return canWill;
      if (plan === "trust" || plan === "joint_trust") return canTrust ;
      return true; // "attorney" and "skip" always allowed
    },
    [canWill, canTrust]
  );

  // ---------------------------------------------------------------
  // 🏷️  Pricing – set these once or pull from an API / env var
  // ---------------------------------------------------------------
  const trustPrice =
    selectedHousehold?.household_role?.professional_pricing_option === "CUSTOM"
      ? (selectedHousehold?.household_role?.trust_price || 0) / 100
      : (selectedHousehold?.professional_group?.default_trust_price || 0) / 100; // price for ONE Revocable Living Trust package
  const willPrice =
    selectedHousehold?.household_role?.professional_pricing_option === "CUSTOM"
      ? (selectedHousehold?.household_role?.will_price || 0) / 100
      : (selectedHousehold?.professional_group?.default_will_price || 0) / 100; // price for ONE Last Will & Testament package

  // utils/money.ts
  const formatUSD = (amount: number): string => {
    const wholeNumber = amount % 1 === 0;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: wholeNumber ? 0 : 2,
      maximumFractionDigits: wholeNumber ? 0 : 2,
    });

    return formatter.format(amount);
  };

  // Utility: how much does this option cost in total?
  const optionCost = (value: string): number => {
    switch (value) {
      case "joint": // one trust for both
        return trustPrice;
      case "trust": // two individual trusts
        if (maritalStatus === "single") {
          return trustPrice;
        } else {
          return trustPrice * 2;
        }
      case "will": // two individual wills
        if (maritalStatus === "single") {
          return willPrice;
        } else {
          return willPrice * 2;
        }
      default: // attorney / skip
        return 0;
    }
  };

  useEffect(() => {
    /* -------- 1️⃣ pick a recommended plan based on goals & family -------- */
    let plan: string;
    let explainer: string;
    // Build goal-based reasons
    const goalReasons: string[] = [];
    if (checkedGoals.avoidProbate) goalReasons.push("avoiding probate");
    if (checkedGoals.privacy) goalReasons.push("maintaining privacy");
    if (checkedGoals.minimizeTaxes) goalReasons.push("minimizing taxes");
    if (checkedGoals.guardianship)
      goalReasons.push("naming guardians for minor children");
    if (checkedGoals.incapacity) goalReasons.push("planning for incapacity");

    const goalsText =
      goalReasons.length > 0
        ? ` This plan is well-suited for ${
            goalReasons.length === 1
              ? goalReasons[0]
              : goalReasons.slice(0, -1).join(", ") +
                ", and " +
                goalReasons[goalReasons.length - 1]
          }.`
        : "";

    // Determine the recommended plan based on goals and family situation
    if (
      checkedGoals.easySetup &&
      !checkedGoals.avoidProbate &&
      !checkedGoals.privacy
    ) {
      plan = "will";
      explainer = `Many people in your situation use a Last Will & Testament as a quick, easy, and solid foundation.${goalsText}`;
    } else {
      if (maritalStatus === "single") {
        plan = "trust";
        explainer = `Many people in your situation use a Living Trust as the foundation of their estate plan.${goalsText}`;
      } else if (blendedFamily === "blended") {
        plan = "trust";
        explainer = `Many people with blended families use separate Individual Living Trusts to specify different sets of beneficiaries.${goalsText}`;
      } else {
        plan = "joint_trust";
        explainer = `Many people in your situation use a Joint Living Trust as the foundation of their estate plan.${goalsText}`;
      }
    }

    /* -------- 2️⃣ if that candidate isn't allowed, fall back -------- */
    if (!isPlanAllowed(plan)) {
      if (plan === "will" && canTrust)
        plan = maritalStatus === "single" ? "trust" : "joint_trust";
      else if ((plan === "trust" || plan === "joint_trust") && canWill) plan = "will";
    }

    /* -------- 2.5️⃣ Louisiana exception: never recommend trust-based plans -------- */
    // Louisiana uses a civil law system (Napoleonic Code) that makes trusts less straightforward.
    // Users can still choose a trust, but we should not recommend it as "Best Fit".
    if (stateOfResidence === "LA" && canWill) {
      if (plan === "trust" || plan === "joint_trust") {
        plan = "will";
        explainer = `Many people in Louisiana use a Last Will & Testament as the foundation of their estate plan.${goalsText}`;
      }
    }

    /* -------- 3️⃣ commit to state -------- */
    setRecommendedPlan(plan);
    setRecommendedPlanExplainer(explainer);

    // Set the appropriate trust type based on the recommendation
    if (plan === "trust") {
      if (maritalStatus === "single") {
        setTrustType("single_trust"); // 1x Individual Living Trust for singles
      } else if (blendedFamily === "blended") {
        setTrustType("two_trusts"); // 2x Individual Living Trusts for blended families
      } else {
        setTrustType("joint"); // Joint Living Trust for non-blended couples
      }
    } else if (plan === "joint_trust") {
      setTrustType("joint");
    }
    // setPersonalPlan(plan);          // keep the "default selected" behaviour
  }, [
    maritalStatus,
    childrenStatus,
    blendedFamily,
    checkedGoals,
    stateOfResidence,
    canWill,
    canTrust,
    isPlanAllowed,
  ]);

  const [address, setAddress] = useState("");
  const [householdStateCode, setHouseholdStateCode] = useState("");

  const handleAddressChange = (value: string) => {
    setAddress(value);
  };

  const handleAddressSelect = async (value: string) => {
    setAddress(value);
    const { state_code } = await parseStateAndPostalCode(value);
    setHouseholdStateCode(state_code);
    setStateOfResidence(state_code);
  };

  useEffect(() => {
    const addressText =
      selectedHousehold?.head_of_household?.address_text || "";
    const stateCode =
      selectedHousehold?.head_of_household?.household_state_code || "";

    setAddress(addressText);
    setHouseholdStateCode(stateCode);

    // Also update stateOfResidence if there's an existing state code
    if (stateCode) {
      setStateOfResidence(stateCode);
    } else if (addressText && !stateCode) {
      // If address exists but no state code, extract it from the address
      parseStateAndPostalCode(addressText)
        .then(({ state_code }) => {
          if (state_code) {
            setHouseholdStateCode(state_code);
            setStateOfResidence(state_code);
          }
        })
        .catch((err) => {
          console.error("Error parsing state from pre-filled address:", err);
        });
    }
  }, [selectedHousehold]);

  // Pre-populate spouse fields if spouse PeopleRole exists
  useEffect(() => {
    if (spouse_role) {
      setMaritalStatus(spouse_role.spouse_relationship || "");
      if (isLoggedInUserSpouse) {
        setSpouseName(headOfHousehold?.full_name || "");
        setSpouseEmail(headOfHousehold?.contact_email || "");
      } else if (targetUserData) {
        setSpouseName(targetUserData.full_name || "");
        setSpouseEmail(targetUserData.contact_email || "");
      }
    }
  }, [spouse_role, targetUserData, headOfHousehold, isLoggedInUserSpouse]);

  // Show logo loader only if on that step
  if (onboardingStep === "logo_loader") {
    return (
      <ClassicLayout containerMaxWidth="sm">
        <Seo title="Estate Planning Onboarding" />
        <LogoLoader />
      </ClassicLayout>
    );
  }

  // Show plan recommendation loader
  if (onboardingStep === "loader") {
    return (
      <ClassicLayout containerMaxWidth="sm">
        <Seo title="Estate Planning Onboarding" />
        <LogoLoader
          messages={[
            "Matching your estate planning goals...",
            "Analyzing your family situation...",
            "Reviewing special circumstances...",
            "Evaluating financial considerations...",
            "Selecting optimal plan recommendations...",
          ]}
        />
      </ClassicLayout>
    );
  }

  // Show plan recommendation loader
  if (onboardingStep === "personal_plan") {
    return (
      <ClassicLayout containerMaxWidth="md">
        <Seo title="Estate Planning Onboarding" />
        <PersonalPlanSelector
          maritalStatus={maritalStatus}
          recommendedPlan={recommendedPlan}
          recommendedPlanExplainer={recommendedPlanExplainer}
          willType={willType}
          trustType={trustType}
          checkedGoals={checkedGoals}
          checkedSituations={checkedSituations}
          // includeMembership={includeMembership}
          // setIncludeMembership={setIncludeMembership}
          setWillType={setWillType}
          setTrustType={setTrustType}
          onSavePlan={async (
            foundationValue,
            // includeMembership
          ) => {
            await onboardingHandleEditHoHUserData(headOfHousehold?.ud_id!, {
              estate_plan_foundation: foundationValue,
              show_household_onboarding_requirement: false,
              // include_membership: includeMembership,
            });
          }}
          onBack={() => {
            // Go back to the last step before personal_plan
            // If premium was shown, go to access, otherwise go to situations
            // if (subscriptionStatus === "offered") {
            //   setOnboardingStep("access");
            // } else {
            //   setOnboardingStep("situations");
            // }
            setOnboardingStep("situations");
          }}
          formatUSD={formatUSD}
          optionCost={optionCost}
          saving={headOfHouseholDataSaving}
        />
      </ClassicLayout>
    );
  }

  return (
    <ClassicLayout
      containerMaxWidth={onboardingStep === "personal_plan" ? "lg" : "sm"}
    >
      <Seo title="Estate Planning Onboarding" />
      <div style={{ position: "relative" }}>
        <Stack direction="column" spacing={4}>
          {onboardingStep === "household" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">Create a new household</Typography>
              <Typography variant="body2" color="text.secondary">
                It is important to coordinate estate plans across the household.
                Whether it's just you or the whole family, we have you covered.
              </Typography>
            </Stack>
          )}

          {onboardingStep === "spouse_name" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">
                What is your{" "}
                {maritalStatus === "married" ? `spouse's` : `partner's`} name?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We'll add your{" "}
                {maritalStatus === "married" ? `spouse` : `partner`} to the
                account and your personalized document checklist.
              </Typography>
            </Stack>
          )}
          {onboardingStep === "marital_status" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">Are you married?</Typography>
              <Typography variant="body2" color="text.secondary">
                We'll help make sure the whole family is covered with a
                coordinated estate plan.
              </Typography>
            </Stack>
          )}

          {onboardingStep === "children_status" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">Do you have children?</Typography>
              <Typography variant="body2" color="text.secondary">
                A well-designed estate plan can cover children of any age,
                whether minors or adults.
              </Typography>
            </Stack>
          )}
          {onboardingStep === "blended_family" &&
            headOfHousehold?.children &&
            headOfHousehold.children !== "none" && (
              <Stack spacing={1} sx={{ textAlign: "center" }}>
                <Typography variant="h5">Are you a blended family?</Typography>
                <Typography variant="body2" color="text.secondary">
                  There are special strategies available if you or your{" "}
                  {maritalStatus === "married" ? `spouse` : `partner`} have
                  children from past relationships.
                </Typography>
              </Stack>
            )}
          {onboardingStep === "residence" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">Where do you live?</Typography>
              <Typography variant="body2" color="text.secondary">
                Every document will be tailored to your local state law,
                including any estate or inheritance taxes.
              </Typography>
            </Stack>
          )}
          {onboardingStep === "goals" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">
                Choose what is most important
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your estate plan will be designed to meet your personal goals.
              </Typography>
            </Stack>
          )}
          {onboardingStep === "situations" && (
            <Stack spacing={1} sx={{ textAlign: "center" }}>
              <Typography variant="h5">
                Check for any special situations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These situations can lead to a more complex estate that may
                benefit from an estate planning attorney.
              </Typography>
            </Stack>
          )}
          {/* {onboardingStep === "personal_plan" && (
                    <Stack spacing={1} sx={{textAlign:'center'}}>
                      <Typography variant="h5">Choose your plan</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Wills and Trusts are the foundation of any estate plan.
                        You can always change your mind later.
                      </Typography>
                    </Stack>
                  )} */}
          <Box sx={{ width: "100%", px: 3 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Card
            variant="outlined"
            sx={{
              border: "1px solid #e7e7e7",
              borderRadius: 1,
              p: {
                xs: 1, // padding on extra small screens
                sm: 1, // padding on small screens
                md: 2, // padding on medium screens
                lg: 2, // padding on large screens
                xl: 2, // padding on extra large screens
              },
              px: {
                xs: 1, // padding on extra small screens
                sm: 1, // padding on small screens
                md: 1, // padding on medium screens
                lg: 1, // padding on large screens
                xl: 1, // padding on extra large screens
              },
              pb: 4,
              position: "relative",
              overflow: "visible",
            }}
          >
            <CardContent sx={{ pt: 2 }}>
              {onboardingStep === "household" && (
                <Stack spacing={2.5}>
                  {/* Your Name */}
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      What is your name?
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Your name"
                      name="your_name"
                      value={isLoggedInUserSpouse ? (userData?.full_name || "") : (headOfHousehold?.full_name || "")}
                      onChange={(e) => {
                        if (isLoggedInUserSpouse && userData) {
                          handleAddOrEditOwnerUserData({ ...userData, full_name: e.target.value });
                        } else if (!isLoggedInUserSpouse && headOfHousehold?.ud_id) {
                          onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              full_name: e.target.value,
                            }
                          );
                        }
                      }}
                      InputProps={{
                        sx: {
                          height: "40px",
                          "& input": {
                            p: "10px 14px",
                          },
                        },
                      }}
                    />
                  </Stack>

                  {/* State of Residence */}
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Where do you live?
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        name="state_of_residence"
                        value={stateOfResidence || ""}
                        onChange={(e) => {
                          const newState = e.target.value;
                          setStateOfResidence(newState);
                          if (headOfHousehold?.ud_id) {
                            onboardingHandleEditHoHUserData(
                              headOfHousehold.ud_id,
                              {
                                household_state_code: newState,
                              }
                            );
                          }
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <Typography
                                color="text.secondary"
                                variant="subtitle2"
                              >
                                Select your state
                              </Typography>
                            );
                          }
                          const state = US_STATES.find(
                            (s) => s.code === selected
                          );
                          return state ? state.name : selected;
                        }}
                      >
                        {US_STATES.map((state) => (
                          <MenuItem key={state.code} value={state.code}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  {/* Marital Status */}
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Are you married?
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        name="marital_status"
                        value={maritalStatus || ""}
                        onChange={(e) => handleChangeMaritalStatus(e as any)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <Typography
                                color="text.secondary"
                                variant="subtitle2"
                              >
                                Select marital status
                              </Typography>
                            );
                          }
                          const labels: Record<string, string> = {
                            single: "Single",
                            married: "Married",
                            domestic_partnership: "Domestic Partnership",
                            civil_union: "Civil Union",
                          };
                          return labels[selected] || selected;
                        }}
                      >
                        <MenuItem value="single">Single</MenuItem>
                        <MenuItem value="married">Married</MenuItem>
                        <MenuItem value="domestic_partnership">
                          Domestic Partnership
                        </MenuItem>
                        <MenuItem value="civil_union">Civil Union</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  {/* Spouse/Partner Name & Email */}
                  {maritalStatus && maritalStatus !== "single" && (
                    <Stack spacing={2.5}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {[
                            "married",
                            "domestic_partnership",
                            "civil_union",
                          ].includes(maritalStatus)
                            ? "What is your spouse's name?"
                            : "What is your partner's name?"}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={
                            [
                              "married",
                              "domestic_partnership",
                              "civil_union",
                            ].includes(maritalStatus)
                              ? "Spouse name"
                              : "Partner name"
                          }
                          name="spouse_name"
                          value={spouseName}
                          onChange={(e) => {
                            setSpouseName(e.target.value);
                            setSpouseNameError("");
                          }}
                          error={!!spouseNameError}
                          helperText={spouseNameError}
                          InputProps={{
                            sx: {
                              height: "40px",
                              "& input": {
                                p: "10px 14px",
                              },
                            },
                          }}
                        />
                      </Stack>

                      {/* Switch and Email Field */}
                      {!hasExistingSpouseEmail && (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={showSpouseEmail}
                                onChange={(e) =>
                                  setShowSpouseEmail(e.target.checked)
                                }
                              />
                            }
                            label={
                              !showSpouseEmail && (
                                <Typography variant="body2">
                                  {[
                                    "married",
                                    "domestic_partnership",
                                    "civil_union",
                                  ].includes(maritalStatus)
                                    ? "Add spouse email?"
                                    : "Add partner email?"}
                                </Typography>
                              )
                            }
                            sx={{ m: 0, flexShrink: 0 }}
                          />

                          {showSpouseEmail && (
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={
                                [
                                  "married",
                                  "domestic_partnership",
                                  "civil_union",
                                ].includes(maritalStatus)
                                  ? "Spouse email"
                                  : "Partner email"
                              }
                              name="spouse_email"
                              type="email"
                              value={spouseEmail}
                              onChange={(e) => {
                                setSpouseEmail(e.target.value);
                                setSpouseEmailError("");
                              }}
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value && !/^\S+@\S+\.\S+$/.test(value)) {
                                  setSpouseEmailError(
                                    "Please enter a valid email address"
                                  );
                                } else if (
                                  value &&
                                  value.toLowerCase() ===
                                    headOfHousehold?.contact_email?.toLowerCase()
                                ) {
                                  setSpouseEmailError(
                                    "Spouse email cannot be the same as your email"
                                  );
                                } else {
                                  setSpouseEmailError("");
                                }
                              }}
                              error={!!spouseEmailError}
                              helperText={spouseEmailError}
                              InputProps={{
                                sx: {
                                  height: "40px",
                                  "& input": {
                                    p: "10px 14px",
                                  },
                                },
                              }}
                            />
                          )}
                        </Stack>
                      )}
                    </Stack>
                  )}

                  {/* Children Status */}
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Do you have children?
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        name="children_status"
                        value={childrenStatus || ""}
                        onChange={(e) => handleChangeChildrenStatus(e as any)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <Typography
                                color="text.secondary"
                                variant="subtitle2"
                              >
                                Select children status
                              </Typography>
                            );
                          }
                          const labels: Record<string, string> = {
                            none: "No children",
                            adult: "All children over 18",
                            minor: "At least one child under 18",
                          };
                          return labels[selected] || selected;
                        }}
                      >
                        <MenuItem value="none">No children</MenuItem>
                        <MenuItem value="adult">All children over 18</MenuItem>
                        <MenuItem value="minor">
                          At least one child under 18
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  {/* Blended Family */}
                  {childrenStatus &&
                    childrenStatus !== "none" &&
                    maritalStatus !== "single" && (
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Are you a blended family?
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            name="blended_family"
                            value={blendedFamily || ""}
                            onChange={(e) =>
                              handleChangeBlendedFamily(e.target.value)
                            }
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <Typography
                                    color="text.secondary"
                                    variant="subtitle2"
                                  >
                                    Select blended family status
                                  </Typography>
                                );
                              }
                              const labels: Record<string, string> = {
                                blended: "Yes, we are a blended family",
                                not: "No, all children are ours together",
                              };
                              return labels[selected] || selected;
                            }}
                          >
                            <MenuItem value="blended">
                              Yes, we are a blended family
                            </MenuItem>
                            <MenuItem value="not">
                              No, all children are ours together
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    )}
                </Stack>
              )}
              {onboardingStep === "marital_status" && (
                <Stack spacing={3}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      name="marital_status"
                      value={maritalStatus}
                      onChange={handleChangeMaritalStatus}
                    >
                      <FormControlLabel
                        value="single"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Single
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="married"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Married
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="domestic_partnership"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Domestic Partnership
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="civil_union"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Civil Union
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  {maritalStatus && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                        border: "1px solid #e7e7e7",
                        borderRadius: 1,
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": {
                            paddingBottom: 3,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            <Typography sx={{ mt: 1, flex: 1 }} variant="body2">
                              Most{" "}
                              {maritalStatus === "single"
                                ? `people`
                                : `couples`}{" "}
                              can complete all of their estate planning
                              documents in under{" "}
                              {maritalStatus === "single" ? `20` : `30`}{" "}
                              minutes. We'll guide you each step of the way.
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  {maritalStatus === "single" ? (
                    <LoadingButton
                      disabled={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        !maritalStatus
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={peopleRolesSavingNew || peopleRolesLoading}
                      onClick={async () => {
                        if (!headOfHousehold?.ud_id) return;
                        setOnboardingStep("children_status");
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      disabled={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        !maritalStatus
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={peopleRolesLoading || peopleRolesSavingNew}
                      onClick={async () => {
                        if (!headOfHousehold?.ud_id) return;
                        setOnboardingStep("spouse_name");
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                  )}
                </Stack>
              )}
              {onboardingStep === "spouse_name" && (
                <Stack spacing={3}>
                  <Stack spacing={1} sx={{ width: "100%" }}>
                    <TextField
                      autoFocus
                      error={!!spouseNameError}
                      fullWidth
                      helperText={spouseNameError}
                      label={
                        [
                          "married",
                          "domestic_partnership",
                          "civil_union",
                        ].includes(maritalStatus)
                          ? `Spouse name`
                          : `Partner name`
                      }
                      name="spouse_name"
                      onChange={(e) => {
                        setSpouseName(e.target.value);
                        setSpouseNameError("");
                      }}
                      type="name"
                      value={spouseName}
                    />
                    <TextField
                      fullWidth
                      label={
                        [
                          "married",
                          "domestic_partnership",
                          "civil_union",
                        ].includes(maritalStatus)
                          ? `Spouse email`
                          : `Partner email`
                      }
                      name="spouse_email"
                      type="email"
                      value={spouseEmail}
                      onChange={(e) => {
                        setSpouseEmail(e.target.value);
                        setSpouseEmailError("");
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (!value) {
                          setSpouseEmailError("Email is required");
                        } else if (!/^\S+@\S+\.\S+$/.test(value)) {
                          setSpouseEmailError(
                            "Please enter a valid email address"
                          );
                        } else if (
                          value.toLowerCase() ===
                          headOfHousehold?.contact_email?.toLowerCase()
                        ) {
                          setSpouseEmailError(
                            "Spouse email cannot be the same as your email"
                          );
                        } else {
                          setSpouseEmailError("");
                        }
                      }}
                      error={!!spouseEmailError}
                      helperText={spouseEmailError}
                    />
                  </Stack>
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={
                        peopleRolesLoading ||
                        !!spouseEmailError ||
                        peopleRolesSavingNew ||
                        !spouseName ||
                        !spouseEmail
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={peopleRolesLoading || peopleRolesSavingNew}
                      onClick={async () => {
                        // Validate spouse name
                        if (!spouseName) {
                          setSpouseNameError("Spouse name is required");
                          return;
                        }
                        // Validate spouse email
                        if (!spouseEmail) {
                          setSpouseEmailError("Email is required");
                          return;
                        }
                        if (!/^\S+@\S+\.\S+$/.test(spouseEmail)) {
                          setSpouseEmailError(
                            "Please enter a valid email address"
                          );
                          return;
                        }
                        if (
                          spouseEmail.toLowerCase() ===
                          headOfHousehold?.contact_email?.toLowerCase()
                        ) {
                          setSpouseEmailError(
                            "Spouse email cannot be the same as your email"
                          );
                          return;
                        }
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          // Save or update spouse PeopleRole
                          await handleAddOrUpdatePeopleRolesAndUserData(
                            headOfHousehold.ud_id,
                            {
                              id: spouse_role?.id || null,
                              role: "SPOUSE",
                              spouse_relationship: [
                                "married",
                                "domestic_partnership",
                                "civil_union",
                              ].includes(maritalStatus)
                                ? (maritalStatus as SpouseRelationshipTypes)
                                : undefined,
                              is_household_member: true,
                              role_target_user_data:
                                spouse_role?.role_target_user_data_ud_id
                                  ? {
                                      ud_id:
                                        spouse_role.role_target_user_data_ud_id,
                                      full_name: spouseName,
                                      contact_email: spouseEmail,
                                    }
                                  : {
                                      full_name: spouseName,
                                      contact_email: spouseEmail,
                                    },
                            }
                          );
                          setOnboardingStep("children_status");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => {
                        setOnboardingStep("marital_status");
                        setMode("edit");
                      }}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "children_status" && (
                <Stack spacing={3}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      name="children_status"
                      value={childrenStatus}
                      onChange={handleChangeChildrenStatus}
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            No children
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="adult"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            All children over 18
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="minor"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            At least one child under 18
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  {childrenStatus && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                        border: "1px solid #e7e7e7",
                        borderRadius: 1,
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": {
                            paddingBottom: 3,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            {childrenStatus === "none" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                It is easy to create a custom list of
                                beneficiaries, including family, friends, and
                                charitable organizations.
                              </Typography>
                            )}
                            {childrenStatus === "adult" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                You will be able to assign adult children as
                                beneficiaries, executors, trustees, power of
                                attorney agents, and more.
                              </Typography>
                            )}
                            {childrenStatus === "minor" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                You will be able to assign backup guardians and
                                manage the inheritance for any minor children in
                                your family.
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={headOfHouseholDataSaving || !childrenStatus}
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              children: childrenStatus,
                            }
                          );
                          // Skip blended family step if no children
                          if (childrenStatus === "none") {
                            setOnboardingStep("residence");
                          } else {
                            setOnboardingStep("blended_family");
                          }
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    {maritalStatus === "single" ? (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("marital_status")}
                      >
                        Back
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("spouse_name")}
                      >
                        Back
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "blended_family" &&
                headOfHousehold?.children &&
                headOfHousehold.children !== "none" && (
                  <Stack spacing={3}>
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="blended_family"
                        value={blendedFamily}
                        onChange={(e) =>
                          handleChangeBlendedFamily(e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="blended"
                          control={<Radio size="small" />}
                          label={
                            <Typography color="text.secondary" variant="body2">
                              Yes, we are a blended family
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="not"
                          control={<Radio size="small" />}
                          label={
                            <Typography color="text.secondary" variant="body2">
                              No, all children are ours together
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    {blendedFamily && (
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: "#FAF9F8",
                          border: "1px solid #e7e7e7",
                          borderRadius: 1,
                        }}
                      >
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            p: 2,
                            "&:last-child": {
                              paddingBottom: 3,
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems={"center"}
                          >
                            <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                              <CheckCircleSolidIcon />
                            </SvgIcon>
                            <Stack direction="column" spacing={1}>
                              {blendedFamily === "blended" && (
                                <Typography
                                  sx={{ mt: 1, flex: 1 }}
                                  variant="body2"
                                >
                                  Great, planning for a blended family typically
                                  uses individual Wills or Trusts to ensure each
                                  set of children is properly set up and
                                  protected as beneficiaries.
                                </Typography>
                              )}
                              {blendedFamily === "not" && (
                                <Typography
                                  sx={{ mt: 1, flex: 1 }}
                                  variant="body2"
                                >
                                  Great, you'll have a full range of options for
                                  your estate planning, including a Joint Trust
                                  or Individual Wills/Trusts for you and your{" "}
                                  {maritalStatus === "married"
                                    ? `spouse`
                                    : `partner`}
                                  .
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                    <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                      <LoadingButton
                        disabled={headOfHouseholDataSaving || !blendedFamily}
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        size="large"
                        sx={{ mt: 4 }}
                        type="button"
                        variant="contained"
                        loading={headOfHouseholDataSaving}
                        onClick={async () => {
                          try {
                            if (!headOfHousehold?.ud_id) return;
                            await onboardingHandleEditHoHUserData(
                              headOfHousehold.ud_id,
                              {
                                blended_family:
                                  blendedFamily === "blended" ? true : false,
                              }
                            );
                            setOnboardingStep("residence");
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        {`Save and continue`}
                      </LoadingButton>
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("children_status")}
                      >
                        Back
                      </Button>
                    </Stack>
                  </Stack>
                )}
              {onboardingStep === "residence" && (
                <Stack spacing={3}>
                  <Stack spacing={1} sx={{ width: "100%" }}>
                    <PlacesAutocomplete
                      key="account_address"
                      value={address}
                      onChange={handleAddressChange}
                      onSelect={handleAddressSelect}
                      searchOptions={{
                        types: ["address"],
                        componentRestrictions: { country: "us" },
                      }}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div
                          style={{
                            width: "100%",
                            textAlign: "left",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "100%",
                            }}
                          >
                            <div className="address-div">
                              <TextField
                                fullWidth
                                label="Address"
                                name="addressautocomplete"
                                {...getInputProps({
                                  placeholder: "Enter your address",
                                  className:
                                    "location-search-input address-input",
                                  autoComplete: "new-user-street-address",
                                })}
                              />
                            </div>
                            <div
                              className="autocomplete-dropdown-container"
                              style={{ backgroundColor: "white" }}
                            >
                              {loading && <></>}
                              {suggestions.map((suggestion) => {
                                const className = suggestion.active
                                  ? "suggestion-item--active"
                                  : "suggestion-item";
                                const style = suggestion.active
                                  ? {
                                      backgroundColor: "#fafafa",
                                      cursor: "pointer",
                                      padding: "5px 15px",
                                      marginBottom: 6,
                                    }
                                  : {
                                      backgroundColor: "#ffffff",
                                      cursor: "pointer",
                                      padding: "10px 15px",
                                      marginBottom: 6,
                                    };
                                return (
                                  <div
                                    {...getSuggestionItemProps(suggestion, {
                                      className,
                                      style,
                                    })}
                                    key={suggestion.placeId}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Stack>
                  {summary && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                        border: "1px solid #e7e7e7",
                        borderRadius: 1,
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": { paddingBottom: 3 },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            <Typography sx={{ mt: 1, flex: 1 }} variant="body2">
                              <b>We have you covered!</b> {summary}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={
                        headOfHouseholDataSaving ||
                        !address ||
                        !householdStateCode
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              address_text: address,
                              household_state_code: householdStateCode,
                            }
                          );
                          setOnboardingStep("goals");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    {childrenStatus === "none" ? (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("children_status")}
                      >
                        Back
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("blended_family")}
                      >
                        Back
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "goals" && (
                <Stack spacing={2.5}>
                  <Stack spacing={2} sx={{ ml: -1 }}>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checkedGoals.avoidProbate}
                        name="avoid_probate"
                        onChange={handleGoalChange("avoidProbate")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        <b>Avoid Probate</b> - I want my beneficiaries to
                        receive their inheritance quickly.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checkedGoals.privacy}
                        name="privacy"
                        onChange={handleGoalChange("privacy")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        <b>Privacy</b> - I want my estate plans to be private
                        and kept out of the public record.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checkedGoals.minimizeTaxes}
                        name="minimize_taxes"
                        onChange={handleGoalChange("minimizeTaxes")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        <b>Minimize Taxes</b> - I want to minimize any federal
                        or state taxes, either on the estate or inheritance.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checkedGoals.easySetup}
                        name="easy_setup"
                        onChange={handleGoalChange("easySetup")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        <b>Easy Setup</b> - I want a quick and easy option, even
                        if that means more work down the road for family.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checkedGoals.guardianship}
                        name="guardianship"
                        onChange={handleGoalChange("guardianship")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        <b>Guardianship</b> - I want to specify backup guardians
                        for any minor children.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checkedGoals.incapacity}
                        name="incapacity"
                        onChange={handleGoalChange("incapacity")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        <b>Incapacity</b> - I want to choose who can make
                        financial and medical decisions if I can't.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "situations" && (
                <Stack spacing={2.5}>
                  <Stack spacing={0} sx={{ ml: -1 }}>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.nonUs}
                        name="non_us"
                        onChange={handleSituationChange("nonUs")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        I am not a US citizen.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.spouseNonUs}
                        name="spouse_non_us"
                        onChange={handleSituationChange("spouseNonUs")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        I am married to a non-US citizen.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.charity}
                        name="charity"
                        onChange={handleSituationChange("charity")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        I want to make a large, tax-advantaged donation.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.international}
                        name="international"
                        onChange={handleSituationChange("international")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        I own significant property in another country.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.creditors}
                        name="creditors"
                        onChange={handleSituationChange("creditors")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        I am being sued, or am concerned about creditors.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.fight}
                        name="fight"
                        onChange={handleSituationChange("fight")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        I believe my family will fight over my estate.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Checkbox
                        checked={checkedSituations.none}
                        name="none"
                        onChange={handleSituationChange("none")}
                      />
                      <Typography
                        color="text.primary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        None of these apply to me.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              )}

              {onboardingStep === "personal_plan_old" && (
                <Stack spacing={3}>
                  <Stack spacing={2}>
                    {[...personalPlanOptionsAllowed]
                      .sort((a, b) => {
                        if (a.value === recommendedPlan) return -1;
                        if (b.value === recommendedPlan) return 1;
                        return 0;
                      })
                      .map((option) => (
                        <>
                          {!(
                            maritalStatus === "single" &&
                            option.value === "joint_trust"
                          ) && (
                            <Card
                              sx={{
                                alignItems: "center",
                                cursor: "pointer",
                                display: "flex",
                                p: 1.2,
                                border: "1px solid #e7e7e7",
                                borderRadius: 1,
                                ...(personalPlan === option.value && {
                                  backgroundColor: "primary.alpha12",
                                  boxShadow: (theme) =>
                                    `${theme.palette.primary.main} 0 0 0 1px`,
                                }),
                              }}
                              onClick={(e) => {
                                setPersonalPlan(option.value);
                              }}
                              variant="outlined"
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ width: "100%" }}
                              >
                                <Radio
                                  checked={personalPlan === option.value}
                                  color="primary"
                                />
                                <Stack spacing={1} sx={{ width: "100%" }}>
                                  <Stack
                                    direction="row"
                                    justifyContent={"space-between"}
                                    alignItems={"center"}
                                    sx={{ width: "100%" }}
                                  >
                                    {option.value === "attorney" ? (
                                      <Typography variant="subtitle2">
                                        {option.title}
                                      </Typography>
                                    ) : (
                                      <Typography variant="subtitle2">
                                        {option.title}
                                        {maritalStatus !== "single" &&
                                          option.value !== "joint_trust" &&
                                          `s (`}
                                        {maritalStatus !== "single" &&
                                          option.value === "joint_trust" &&
                                          ` (`}
                                        {maritalStatus !== "single" &&
                                          option.plural}
                                        {maritalStatus !== "single" && ")"}
                                      </Typography>
                                    )}
                                    {recommendedPlan === option.value ? (
                                      <SeverityPill
                                        color="success"
                                        sx={{ textTransform: "none" }}
                                      >
                                        Best Fit
                                      </SeverityPill>
                                    ) : (
                                      <>
                                        {option.value !== "attorney" &&
                                          personalPlan !== option.value &&
                                          selectedHousehold?.household_role
                                            ?.paywall_option !==
                                            "direct" &&
                                          selectedHousehold?.professional_group
                                            ?.allow_client_charges && (
                                            <Typography variant="body2">
                                              {formatUSD(
                                                optionCost(option.value)
                                              )}
                                            </Typography>
                                          )}
                                      </>
                                    )}
                                  </Stack>
                                  {(personalPlan === option.value ||
                                    recommendedPlan === option.value) && (
                                    <>
                                      {recommendedPlan === option.value ? (
                                        <Typography
                                          color="text.secondary"
                                          variant="body2"
                                        >
                                          {recommendedPlanExplainer}
                                        </Typography>
                                      ) : (
                                        <Typography
                                          color="text.secondary"
                                          variant="body2"
                                        >
                                          {option.description}
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                  {option.value !== "attorney" &&
                                    selectedHousehold?.household_role
                                      ?.paywall_option !==
                                      "direct" &&
                                    selectedHousehold?.professional_group
                                      ?.allow_client_charges && (
                                      <>
                                        {personalPlan === option.value && (
                                          <Typography variant="body2">
                                            Total cost:{" "}
                                            {formatUSD(
                                              optionCost(option.value)
                                            )}
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                </Stack>
                              </Stack>
                            </Card>
                          )}
                        </>
                      ))}
                  </Stack>
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={headOfHouseholDataSaving || !personalPlan}
                      fullWidth
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              estate_plan_foundation: personalPlan,
                              show_household_onboarding_requirement: false,
                            }
                          );

                          // Navigate back to the original path if returnPath is provided, otherwise go to dashboard
                          // Navigate back to the original path if returnPath is provided, otherwise go to dashboard
                          let navigationPath =
                            returnPath || paths.dashboard.index;
                          // Add personal plan as another parameter on returnPath if applicable
                          if (
                            personalPlan &&
                            ["joint", "will", "trust"].includes(personalPlan)
                          ) {
                            const separator = navigationPath.includes("?")
                              ? "&"
                              : "?";
                            navigationPath += `${separator}personal_plan=${personalPlan}`;
                          }
                          router.push(navigationPath);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("situations")}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
          {/* Continue Button */}
          <Box sx={{ textAlign: "center" }}>
            {onboardingStep === "household" && (
              <LoadingButton
                disabled={
                  !maritalStatus ||
                  !stateOfResidence ||
                  (maritalStatus !== "single" &&
                    (!spouseName || !!spouseEmailError)) ||
                  !childrenStatus ||
                  (childrenStatus !== "none" &&
                    maritalStatus !== "single" &&
                    !blendedFamily) ||
                  peopleRolesLoading ||
                  peopleRolesSavingNew ||
                  headOfHouseholDataSaving
                }
                size="large"
                sx={{ minWidth: "66%", borderRadius: 1 }}
                type="button"
                variant="contained"
                loading={
                  peopleRolesLoading ||
                  peopleRolesSavingNew ||
                  headOfHouseholDataSaving
                }
                onClick={async () => {
                  try {
                    if (!headOfHousehold?.ud_id) return;

                    // Save spouse if not single and logged-in user is HoH (not the spouse)
                    if (maritalStatus !== "single" && !isLoggedInUserSpouse) {
                      if (!spouseName) {
                        setSpouseNameError("Spouse name is required");
                        return;
                      }
                      if (spouseEmail && !/^\S+@\S+\.\S+$/.test(spouseEmail)) {
                        setSpouseEmailError(
                          "Please enter a valid email address"
                        );
                        return;
                      }
                      if (
                        spouseEmail &&
                        spouseEmail.toLowerCase() ===
                          headOfHousehold?.contact_email?.toLowerCase()
                      ) {
                        setSpouseEmailError(
                          "Spouse email cannot be the same as your email"
                        );
                        return;
                      }

                      await handleAddOrUpdatePeopleRolesAndUserData(
                        headOfHousehold.ud_id,
                        {
                          id: spouse_role?.id || null,
                          role: "SPOUSE",
                          spouse_relationship: [
                            "married",
                            "domestic_partnership",
                            "civil_union",
                          ].includes(maritalStatus)
                            ? (maritalStatus as SpouseRelationshipTypes)
                            : undefined,
                          is_household_member: true,
                          role_target_user_data:
                            spouse_role?.role_target_user_data_ud_id
                              ? {
                                  ud_id:
                                    spouse_role.role_target_user_data_ud_id,
                                  full_name: spouseName,
                                  contact_email: spouseEmail,
                                }
                              : {
                                  full_name: spouseName,
                                  contact_email: spouseEmail,
                                },
                        }
                      );
                    }

                    // Save children status
                    await onboardingHandleEditHoHUserData(
                      headOfHousehold.ud_id,
                      {
                        children: childrenStatus,
                      }
                    );

                    // Save blended family if applicable
                    if (
                      childrenStatus !== "none" &&
                      maritalStatus !== "single"
                    ) {
                      await onboardingHandleEditHoHUserData(
                        headOfHousehold.ud_id,
                        {
                          blended_family:
                            blendedFamily === "blended" ? true : false,
                        }
                      );
                    }

                    // Check if this is a direct charge client with estate plan foundation already set
                    const isDirectCharge = selectedHousehold?.household_role?.paywall_option === "direct";
                    const hasEstatePlanFoundation = Boolean(headOfHousehold?.estate_plan_foundation);

                    if (isDirectCharge && hasEstatePlanFoundation) {
                      // Direct charge clients with estate plan foundation go directly to express
                      router.push(paths.dashboard.express);
                    } else {
                      // Show logo loader for 3 seconds before moving to next step
                      setOnboardingStep("logo_loader");
                    }
                  } catch (err) {
                    console.error(err);
                  }
                }}
              >
                Save and continue
              </LoadingButton>
            )}
            {/* {onboardingStep === "premium" &&
              subscriptionStatus === "offered" && (
                <Stack spacing={1}>
                  <Box>
                    <LoadingButton
                      size="large"
                      sx={{ minWidth: "66%", borderRadius: 1 }}
                      type="button"
                      variant="contained"
                      loading={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        headOfHouseholDataSaving
                      }
                      onClick={() => setOnboardingStep("assets")}
                    >
                      Continue
                    </LoadingButton>
                  </Box>
                  <Box>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("situations")}
                    >
                      Back
                    </Button>
                  </Box>
                </Stack>
              )} */}
            {/* {onboardingStep === "assets" &&
              subscriptionStatus === "offered" && (
                <Stack spacing={1}>
                  <Box>
                    <LoadingButton
                      size="large"
                      sx={{ minWidth: "66%", borderRadius: 1 }}
                      type="button"
                      variant="contained"
                      loading={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        headOfHouseholDataSaving
                      }
                      onClick={() => setOnboardingStep("access")}
                    >
                      Continue
                    </LoadingButton>
                  </Box>
                  <Box>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("premium")}
                    >
                      Back
                    </Button>
                  </Box>
                </Stack>
              )} */}
            {/* {onboardingStep === "access" &&
              subscriptionStatus === "offered" && (
                <Stack spacing={1}>
                  <Box>
                    <LoadingButton
                      size="large"
                      sx={{ minWidth: "66%", borderRadius: 1 }}
                      type="button"
                      variant="contained"
                      loading={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        headOfHouseholDataSaving
                      }
                      onClick={() => setOnboardingStep("loader")}
                    >
                      Continue
                    </LoadingButton>
                  </Box>
                  <Box>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("assets")}
                    >
                      Back
                    </Button>
                  </Box>
                </Stack>
              )} */}

            {onboardingStep === "goals" && (
              <Stack spacing={1}>
                <Box>
                  <LoadingButton
                    disabled={headOfHouseholDataSaving}
                    size="large"
                    sx={{ minWidth: "66%", borderRadius: 1 }}
                    type="button"
                    variant="contained"
                    loading={headOfHouseholDataSaving}
                    onClick={async () => {
                      try {
                        if (!headOfHousehold?.ud_id) return;
                        // Convert checkedGoals object to array of selected keys
                        const selectedGoals = Object.keys(checkedGoals).filter(
                          (key) =>
                            checkedGoals[key as keyof typeof checkedGoals]
                        );
                        await onboardingHandleEditHoHUserData(
                          headOfHousehold.ud_id,
                          {
                            onboarding_goals: selectedGoals,
                          }
                        );
                        setOnboardingStep("situations");
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    {`Save and continue`}
                  </LoadingButton>
                </Box>
                <Box>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => {
                      // If coming from loader and premium was skipped, go back to situations
                      setOnboardingStep("household");
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </Stack>
            )}
            {onboardingStep === "situations" && (
              <Stack spacing={1}>
                <Box>
                  <LoadingButton
                    disabled={
                      headOfHouseholDataSaving ||
                      Object.values(checkedSituations).every((v) => !v)
                    }
                    endIcon={<ArrowForwardIcon />}
                    size="large"
                    sx={{ minWidth: "66%", borderRadius: 1 }}
                    type="button"
                    variant="contained"
                    loading={headOfHouseholDataSaving}
                    onClick={async () => {
                      try {
                        if (!headOfHousehold?.ud_id) return;
                        // Convert checkedSituations object to array of selected keys
                        const selectedSituations = Object.keys(
                          checkedSituations
                        ).filter(
                          (key) =>
                            checkedSituations[
                              key as keyof typeof checkedSituations
                            ]
                        );

                        // Save the situations first
                        await onboardingHandleEditHoHUserData(
                          headOfHousehold.ud_id,
                          {
                            special_situations: selectedSituations,
                          }
                        );

                        // Skip premium steps if subscription status is not "offered"
                        // if (subscriptionStatus === "offered") {
                        //   setOnboardingStep("premium");
                        // } else {
                        //   setOnboardingStep("loader");
                        // }
                        setOnboardingStep("loader");
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    {`Save and continue`}
                  </LoadingButton>
                </Box>
                <Box>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => setOnboardingStep("goals")}
                  >
                    Back
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </div>
    </ClassicLayout>
  );
};

export default OnboardingContent;
